import i18next from 'i18next';
import CurrentUser from '../models/CurrentUser';
import { USER_SESSION_KEEP_ALIVE_SECONDS } from '../common/constants';
import settings from '../common/settings';
import { notifyWarning, dismissNotification } from '../utils/notify';

const SESSION_EXPIRY_WARNING_LEAD_SECONDS = 60;
const { inactiveSessionLifespanSeconds } = settings.public;

function resumeTokenKeepAlive() {
  return (store) => {
    let loggedIn = false;
    let hadActivity = false;
    let keepaliveInterval = null;
    let keepalivePromise = null;
    let timelockCheckInterval = null;
    let sessionExpiryWarningTimeout = null;
    let sessionExpiryWarningKey = null;
    let onSessionExpiryNotificationClose = null;

    // Cancels the pending timeout but leaves the notification visible if already shown —
    // the user must explicitly close the notification to dismiss it.
    const cancelSessionExpiryWarning = () => {
      if (sessionExpiryWarningTimeout) {
        clearTimeout(sessionExpiryWarningTimeout);
        sessionExpiryWarningTimeout = null;
      }
    };

    // Forcibly dismisses the notification, used only on logout.
    const forceCloseSessionExpiryWarning = () => {
      cancelSessionExpiryWarning();
      if (sessionExpiryWarningKey) {
        dismissNotification(sessionExpiryWarningKey);
        sessionExpiryWarningKey = null;
      }
    };

    const stopKeepaliveInterval = () => {
      if (keepaliveInterval) {
        clearInterval(keepaliveInterval);
        clearInterval(timelockCheckInterval);
        keepaliveInterval = null;
        timelockCheckInterval = null;
      }
      hadActivity = false;
    };

    const keepaliveTick = () => {
      if (!keepalivePromise) {
        if (!hadActivity) {
          stopKeepaliveInterval();
          if (inactiveSessionLifespanSeconds) {
            const delayMs =
              (inactiveSessionLifespanSeconds -
                SESSION_EXPIRY_WARNING_LEAD_SECONDS) *
              1000;
            sessionExpiryWarningTimeout = setTimeout(() => {
              sessionExpiryWarningKey = notifyWarning({
                message: i18next.t(
                  'confirmations:resumeTokenKeepAlive.message',
                ),
                description: i18next.t(
                  'confirmations:resumeTokenKeepAlive.description',
                ),
                onClose: onSessionExpiryNotificationClose,
              });
            }, delayMs);
          }
        }
        hadActivity = false;
        keepalivePromise = store
          .dispatch(CurrentUser.action.resumeTokenKeepAlive())
          .then(() => {
            keepalivePromise = null;
          })
          .catch((err) => {
            keepalivePromise = null;
            console.error(err);
          });
      }
    };

    const ensureKeepaliveInterval = () => {
      cancelSessionExpiryWarning();
      hadActivity = true;
      if (!keepaliveInterval) {
        keepaliveTick();
        keepaliveInterval = setInterval(
          () => keepaliveTick(),
          1000 * USER_SESSION_KEEP_ALIVE_SECONDS,
        );
      }
    };
    onSessionExpiryNotificationClose = () => {
      sessionExpiryWarningKey = null;
      ensureKeepaliveInterval();
    };

    const ensureLoggedIn = () => {
      if (!loggedIn) {
        loggedIn = true;
        if (window) {
          window.addEventListener('click', ensureKeepaliveInterval);
          window.addEventListener('keydown', ensureKeepaliveInterval);
        }
      }
    };

    const startTimelockCheckInterval = (state) => {
      clearInterval(timelockCheckInterval);
      timelockCheckInterval = setInterval(function () {
        if (!!CurrentUser.select.isLockedInTimeWindow()(state)) {
          clearInterval(timelockCheckInterval);
          store.dispatch(CurrentUser.action.logoutCurrentUser());
        }
      }, 5000);
    };

    const ensureLoggedOut = () => {
      stopKeepaliveInterval();
      forceCloseSessionExpiryWarning();
      if (loggedIn) {
        loggedIn = false;
        if (window) {
          window.removeEventListener('click', ensureKeepaliveInterval);
          window.removeEventListener('keydown', ensureKeepaliveInterval);
        }
      }
    };

    return (next) => (action) => {
      const state = store.getState();
      const isLoggedIn = CurrentUser.select.isLoggedIn()(state);
      if (isLoggedIn) {
        ensureLoggedIn();
        startTimelockCheckInterval(state);
      } else {
        ensureLoggedOut();
      }
      return next(action);
    };
  };
}

export default resumeTokenKeepAlive;
